import React, { useState, useEffect, useContext } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Grid,
  Card,
  CardContent,
  CardActions,
  Chip,
  CircularProgress,
  Alert,
  Tabs,
  Tab,
  TextField,
  List,
  ListItem,
  ListItemText,
  Divider
} from '@mui/material';
import { Add as AddIcon, Assignment as AssignmentIcon, ArrowBack as BackIcon } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { AuthContext } from '../../context/AuthContext';
import { API_URL } from '../../utils/apiConfig';
import FormRenderer from './FormRenderer';
import { useMediaQuery as useMuiMediaQuery } from '@mui/material';
import { useTheme } from '@mui/material/styles';

export default function FormsV2Dashboard() {
  const navigate = useNavigate();
  const { userRole } = useContext(AuthContext);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [tab, setTab] = useState(1); // Default to Personal Forms (tab 1)
  const [templates, setTemplates] = useState([]);
  const [search, setSearch] = useState('');
  const [submissions, setSubmissions] = useState([]);
  const [personalForms, setPersonalForms] = useState([]);
  const [selectedPersonalFormId, setSelectedPersonalFormId] = useState(null);

  const isAdmin = ['admin', 'manager'].includes(userRole);
  const theme = useTheme();
  const isDesktop = useMuiMediaQuery(theme.breakpoints.up('md'));

  useEffect(() => {
    fetchData();
  }, [tab]);

  const fetchData = async () => {
    try {
      setLoading(true);
      setError('');
      const token = localStorage.getItem('token');

      if (tab === 0) {
        // Fetch available templates (v2)
        const resp = await fetch(`${API_URL}/vnext/forms/templates`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        const data = await resp.json();
        if (!resp.ok) throw new Error(data.error || 'Failed to load templates');

        const v2Templates = data.data || [];

        // Also fetch legacy employee forms and adapt as templates for visibility
        let legacyTemplates = [];
        try {
          const legacyResp = await fetch(`${API_URL}/employee_forms`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          const legacyData = await legacyResp.json();
          if (legacyResp.ok && Array.isArray(legacyData)) {
            const slugify = (s) => String(s || '')
              .toLowerCase()
              .replace(/[^a-z0-9]+/g, '-')
              .replace(/(^-|-$)+/g, '');
            legacyTemplates = legacyData.map((f) => ({
              id: `legacy-${f.formId}`,
              slug: slugify(f.formName),
              name: f.formName,
              category: 'general',
              current_version: 1,
              schema_json: { title: f.formName, description: 'Legacy form (opens in existing forms system)' },
              source: 'legacy'
            }));
          }
        } catch (_) {}

        // Merge, prefer v2 when slugs collide
        const seen = new Set();
        const merged = [];
        for (const t of v2Templates) {
          if (!seen.has(t.slug)) { seen.add(t.slug); merged.push(t); }
        }
        for (const lt of legacyTemplates) {
          if (!seen.has(lt.slug)) { seen.add(lt.slug); merged.push(lt); }
        }

        setTemplates(merged);
      } else if (tab === 1) {
        // Fetch personal (assigned) forms for the current user (legacy endpoint)
        const legacyResp = await fetch(`${API_URL}/employee_forms`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        const legacyData = await legacyResp.json();
        if (!legacyResp.ok) throw new Error(legacyData.error || 'Failed to load personal forms');
        const list = Array.isArray(legacyData) ? legacyData : [];
        setPersonalForms(list);
        if (!selectedPersonalFormId && list.length > 0) {
          // Prefer first incomplete form
          const pending = list.find(f => !f.isCompleted);
          setSelectedPersonalFormId((pending || list[0]).formId);
        }
      } else {
        // Fetch my submissions
        const resp = await fetch(`${API_URL}/vnext/forms/my-submissions`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        const data = await resp.json();
        if (!resp.ok) throw new Error(data.error || 'Failed to load submissions');
        setSubmissions(data.data || []);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleTemplateClick = (template) => {
    if (template?.source === 'legacy' || String(template?.id).startsWith('legacy-')) {
      const rawId = String(template.id).replace('legacy-', '');
      navigate(`/v2/forms/legacy/${rawId}`);
      return;
    }
    navigate(`/v2/forms/${template.id}`);
  };

  const handleSubmissionClick = (submissionId, source) => {
    if (source === 'legacy') {
      // Open old form system (if needed)
      navigate(`/employee_forms`);
    } else {
      navigate(`/v2/forms/submissions/${submissionId}`);
    }
  };

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, maxWidth: 1400, mx: 'auto' }}>
      {/* Header with gradient background */}
      <Box
        sx={{
          mb: 4,
          p: { xs: 2.5, md: 3 },
          borderRadius: 2,
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          color: 'white',
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row' },
          justifyContent: 'space-between',
          alignItems: { xs: 'flex-start', sm: 'center' },
          gap: { xs: 2, sm: 0 },
          boxShadow: '0 4px 20px rgba(102, 126, 234, 0.3)'
        }}
      >
        <Box>
          <Typography variant="h4" gutterBottom sx={{ fontWeight: 600, mb: 0.5, fontSize: { xs: '1.75rem', md: '2.125rem' } }}>
            Forms
          </Typography>
          <Typography variant="body2" sx={{ opacity: 0.9, display: { xs: 'none', sm: 'block' } }}>
            Complete your assigned forms or browse available templates
          </Typography>
        </Box>
        {isAdmin && (
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => navigate('/v2/form-builder')}
            sx={{
              bgcolor: 'white',
              color: '#667eea',
              textTransform: 'none',
              fontWeight: 600,
              alignSelf: { xs: 'stretch', sm: 'auto' },
              '&:hover': {
                bgcolor: 'rgba(255,255,255,0.9)'
              }
            }}
          >
            Create Form
          </Button>
        )}
      </Box>

      {/* Tabs with modern styling */}
      <Box sx={{ mb: 3 }}>
        <Tabs
          value={tab}
          onChange={(e, v) => setTab(v)}
          variant="scrollable"
          scrollButtons="auto"
          allowScrollButtonsMobile
          sx={{
            '& .MuiTab-root': {
              textTransform: 'none',
              fontWeight: 500,
              fontSize: '0.95rem',
              minHeight: 48,
              minWidth: { xs: 'auto', sm: 160 },
              px: { xs: 2, sm: 3 }
            },
            '& .Mui-selected': {
              color: '#667eea'
            },
            '& .MuiTabs-indicator': {
              backgroundColor: '#667eea',
              height: 3,
              borderRadius: '3px 3px 0 0'
            }
          }}
        >
          <Tab label="Available Forms" />
          <Tab label="Personal Forms" />
          <Tab label="My Submissions" />
        </Tabs>
        <Divider />
      </Box>

      {/* Content */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          {/* Available Forms Tab */}
          {tab === 0 && (
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  placeholder="Search forms by name or category..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  size="small"
                  sx={{
                    mb: 1,
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                      bgcolor: '#f7fafc',
                      '&:hover': {
                        bgcolor: '#edf2f7'
                      },
                      '&.Mui-focused': {
                        bgcolor: 'white'
                      }
                    }
                  }}
                />
              </Grid>
              {templates.length === 0 ? (
                <Grid item xs={12}>
                  <Paper sx={{ p: 4, textAlign: 'center' }}>
                    <AssignmentIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
                    <Typography variant="h6" gutterBottom>
                      No forms available
                    </Typography>
                    <Typography color="text.secondary" sx={{ mb: 2 }}>
                      {isAdmin
                        ? 'Create your first form using the Form Builder'
                        : 'No forms have been assigned to you yet'}
                    </Typography>
                    {isAdmin && (
                      <Button
                        variant="contained"
                        startIcon={<AddIcon />}
                        onClick={() => navigate('/v2/form-builder')}
                      >
                        Create Form
                      </Button>
                    )}
                  </Paper>
                </Grid>
              ) : (
                templates
                  .filter(t => {
                    const q = search.toLowerCase();
                    if (!q) return true;
                    return (
                      (t.name || '').toLowerCase().includes(q) ||
                      (t.category || '').toLowerCase().includes(q)
                    );
                  })
                  .map((template) => (
                  <Grid item xs={12} sm={6} md={4} key={template.id}>
                    <Card
                      sx={{
                        height: '100%',
                        display: 'flex',
                        flexDirection: 'column',
                        transition: 'all 0.3s ease',
                        border: '1px solid',
                        borderColor: 'divider',
                        '&:hover': {
                          transform: 'translateY(-4px)',
                          boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
                          borderColor: '#667eea'
                        }
                      }}
                    >
                      <CardContent sx={{ flexGrow: 1 }}>
                        <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, color: '#2d3748' }}>
                          {template.name}
                        </Typography>
                        <Box sx={{ mb: 1.5, display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                          <Chip
                            label={template.category || 'general'}
                            size="small"
                            sx={{
                              bgcolor: '#f0f4ff',
                              color: '#667eea',
                              fontWeight: 500
                            }}
                          />
                          <Chip
                            label={`v${template.current_version || 1}`}
                            size="small"
                            sx={{ bgcolor: '#f7fafc', color: '#4a5568' }}
                          />
                          {template.source === 'legacy' && (
                            <Chip
                              label="legacy"
                              size="small"
                              sx={{ bgcolor: '#fef5e7', color: '#d97706' }}
                            />
                          )}
                        </Box>
                        <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.6 }}>
                          {template.schema_json?.description || 'No description'}
                        </Typography>
                      </CardContent>
                      <CardActions sx={{ p: 2, pt: 0 }}>
                        <Button
                          size="small"
                          onClick={() => handleTemplateClick(template)}
                          sx={{
                            textTransform: 'none',
                            fontWeight: 600,
                            color: '#667eea'
                          }}
                        >
                          {template.source === 'legacy' ? 'Open' : 'Fill Out'}
                        </Button>
                        {isAdmin && template.source !== 'legacy' && (
                          <Button
                            size="small"
                            onClick={() => navigate(`/v2/form-builder/${template.id}`)}
                            sx={{
                              textTransform: 'none',
                              color: '#718096'
                            }}
                          >
                            Edit
                          </Button>
                        )}
                      </CardActions>
                    </Card>
                  </Grid>
                ))
              )}
            </Grid>
          )}

          {/* Personal Forms Tab - Desktop split view, Mobile full-screen with back */}
          {tab === 1 && (
            <Box sx={{ display: 'flex', gap: 3 }}>
              {/* Left List Panel - Hidden on mobile when form is selected */}
              {(isDesktop || !selectedPersonalFormId) && (
                <Paper
                  elevation={0}
                  sx={{
                    width: isDesktop ? 340 : '100%',
                    flexShrink: 0,
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 2,
                    overflow: 'hidden'
                  }}
                >
                  {personalForms.length === 0 ? (
                    <Box sx={{ p: 4, textAlign: 'center' }}>
                      <AssignmentIcon sx={{ fontSize: 48, color: '#cbd5e0', mb: 2 }} />
                      <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
                        No personal forms assigned
                      </Typography>
                      <Typography color="text.secondary" variant="body2">
                        When forms are assigned to you, they appear here with status.
                      </Typography>
                    </Box>
                  ) : (
                    <List sx={{ p: 0 }}>
                      {personalForms.map((pf, idx) => (
                        <React.Fragment key={`pf-${pf.formId}`}>
                          <ListItem
                            selected={selectedPersonalFormId === pf.formId}
                            onClick={() => setSelectedPersonalFormId(pf.formId)}
                            sx={{
                              cursor: 'pointer',
                              py: 2,
                              px: 2.5,
                              transition: 'all 0.2s ease',
                              '&.Mui-selected': {
                                bgcolor: '#f0f4ff',
                                borderLeft: '3px solid #667eea',
                                '&:hover': {
                                  bgcolor: '#f0f4ff'
                                }
                              },
                              '&:hover': {
                                bgcolor: '#f7fafc'
                              }
                            }}
                            secondaryAction={
                              <Chip
                                label={pf.isCompleted ? 'Completed' : 'Pending'}
                                size="small"
                                sx={{
                                  bgcolor: pf.isCompleted ? '#d4edda' : '#fff3cd',
                                  color: pf.isCompleted ? '#155724' : '#856404',
                                  fontWeight: 500,
                                  border: 'none'
                                }}
                              />
                            }
                          >
                            <ListItemText
                              primary={pf.formName}
                              primaryTypographyProps={{
                                fontWeight: selectedPersonalFormId === pf.formId ? 600 : 500,
                                fontSize: '0.95rem'
                              }}
                            />
                          </ListItem>
                          {idx < personalForms.length - 1 && <Divider component="li" />}
                        </React.Fragment>
                      ))}
                    </List>
                  )}
                </Paper>
              )}

              {/* Right Renderer Panel - Full width on mobile, side panel on desktop */}
              {selectedPersonalFormId && (
                <Box
                  sx={{
                    flex: 1,
                    width: isDesktop ? 'auto' : '100%',
                    position: isDesktop ? 'static' : 'relative'
                  }}
                >
                  {/* Mobile back button */}
                  {!isDesktop && (
                    <Button
                      startIcon={<BackIcon />}
                      onClick={() => setSelectedPersonalFormId(null)}
                      sx={{
                        mb: 2,
                        textTransform: 'none',
                        color: '#667eea',
                        fontWeight: 600
                      }}
                    >
                      Back to Forms List
                    </Button>
                  )}
                  <FormRenderer overrideLegacyId={selectedPersonalFormId} />
                </Box>
              )}
            </Box>
          )}

          {/* My Submissions Tab */}
          {tab === 2 && (
            <Paper>
              {submissions.length === 0 ? (
                <Box sx={{ p: 4, textAlign: 'center' }}>
                  <Typography variant="h6" gutterBottom>
                    No submissions yet
                  </Typography>
                  <Typography color="text.secondary">
                    You haven't submitted any forms yet
                  </Typography>
                </Box>
              ) : (
                <Box sx={{ p: 2 }}>
                  {submissions.map((sub) => (
                    <Card
                      key={`${sub.source}-${sub.id}`}
                      sx={{ mb: 2, cursor: 'pointer' }}
                      onClick={() => handleSubmissionClick(sub.id, sub.source)}
                    >
                      <CardContent>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                          <Box>
                            <Typography variant="h6">
                              {sub.form_name}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              Submitted: {new Date(sub.submitted_at).toLocaleDateString()}
                            </Typography>
                          </Box>
                          <Box>
                            <Chip
                              label={sub.status}
                              size="small"
                              color={sub.status === 'submitted' ? 'success' : 'default'}
                            />
                            {sub.source === 'legacy' && (
                              <Chip
                                label="legacy"
                                size="small"
                                sx={{ ml: 1 }}
                                variant="outlined"
                              />
                            )}
                          </Box>
                        </Box>
                      </CardContent>
                    </Card>
                  ))}
                </Box>
              )}
            </Paper>
          )}
        </>
      )}
    </Box>
  );
}
